import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Link, Text, Tooltip } from '@fly/core';
import { gitUserName } from '@fly/shared';
import { FlySvgSprite } from '@fly/svg';
import { Avatar } from '@radix-ui/themes';

import { FlyTimeago } from '../FlyTimeago';
import { Search } from '../Search/Search';

export type DataRow = {
  label: string;
  icon?: string;
  activity: string;
  author: string | undefined | null;
  badge?: string;
  description?: string | null;
  topAdditional?: React.ReactNode;
  additional?: React.ReactNode;
  href?: string;
  to?: string;
  versionsCount?: number;
};

function VersionsCountBadge({ count }: { count: number }) {
  const { t } = useTranslation();
  const label = t('app.artifacts.version_count', {
    count,
    defaultValue_one: '{{count}} Version',
    defaultValue_other: '{{count}} Versions',
  });

  return (
    <Tooltip content={label} delayDuration={500}>
      <Badge
        color='accent'
        size='1'
        variant='soft'
        radius='full'
        aria-label={label}
      >
        {count}
      </Badge>
    </Tooltip>
  );
}

export function DataRowItem({
  label,
  icon,
  activity,
  author,
  badge,
  description,
  topAdditional,
  additional,
  href,
  to,
  versionsCount,
}: DataRow) {
  const { i18n } = useTranslation();
  const [isHovered, setLinkHover] = useState(false);
  return (
    <div className='flex min-h-[82px] flex-row gap-fly-3 border-[#e9e9e9] px-fly-5 py-fly-4 not-last:border-b dark:border-[#333]'>
      {icon && (
        <FlySvgSprite name={icon} size={16} className='text-slate-11 m-fly-2' />
      )}
      <div className='flex w-full flex-col gap-fly-2'>
        <div className='flex items-start gap-fly-3'>
          <div className='min-w-0 grow'>
            {href || to ? (
              <div className='flex flex-wrap items-center gap-fly-2'>
                <Link
                  highContrast={!(isHovered && href)}
                  href={href}
                  className='flex flex-row items-center gap-fly-2'
                  to={to}
                  target={href ? '_blank' : undefined}
                  size='3'
                  weight='bold'
                  underline='none'
                  onMouseEnter={() => setLinkHover(true)}
                  onMouseLeave={() => setLinkHover(false)}
                >
                  <Search text={label} />
                  {isHovered && href && (
                    <FlySvgSprite name='external_link' size={14} />
                  )}
                </Link>
                {badge && (
                  <Badge color='neutral' size='1' variant='soft'>
                    {badge}
                  </Badge>
                )}
                {versionsCount != null && (
                  <VersionsCountBadge count={versionsCount} />
                )}
              </div>
            ) : (
              <div className='flex flex-wrap items-center gap-fly-2'>
                <Text size='3' color='neutral' highContrast weight='bold'>
                  <Search text={label} />
                </Text>
                {badge && (
                  <Badge color='neutral' size='1' variant='soft'>
                    {badge}
                  </Badge>
                )}
              </div>
            )}
          </div>
          {topAdditional}
        </div>
        {description && (
          <Text size='2' color='neutral' className='font-medium'>
            <Search text={description} />
          </Text>
        )}
        <div className='flex flex-row items-center gap-fly-2'>
          <FlyTimeago
            className='font-semibold'
            size='3'
            color='neutral'
            date={activity}
            language={i18n.language}
          />
          {author && (
            <>
              <Avatar
                radius='full'
                size='1'
                fallback={gitUserName(author).firstLetter}
              />
              <Text size='3' color='neutral' className='font-semibold'>
                {author}
              </Text>
            </>
          )}
          {additional}
        </div>
      </div>
    </div>
  );
}
